import { gql, useMutation, useQuery } from '@apollo/client';

export type Connector = {
  id?: string;
  name?: string;
  type?: string;
  headers?: { key?: string; value?: string }[];
  host?: string;
  status?: string;
  description?: string;
};

type CreateConnectorInput = {
  name: string;
  type: string;
  headers: { key: string; value: string }[];
  host: string;
  status: string;
  description?: string;
};

type UpdateConnectorInput = {
  id: string;
  name?: string;
  type?: string;
  headers?: { key?: string; value?: string }[];
  host?: string;
  status?: string;
  description?: string;
};

const GET_ALL_CONNECTORS = gql`
  query GetConnectors {
    Connectors {
      edges {
        node {
          id
          name
          host
          headers {
            key
            value
          }
          type
          status
          description
        }
      }
    }
  }
`;

export const useConnectors = () => {
  return useQuery<{ Connectors: { edges: { node: Connector }[] } }>(GET_ALL_CONNECTORS);
};

export const useConnector = (connectorId: string) => {
  return useQuery<{ Connector: Connector }>(
    gql`
      query GetConnector($connectorId: String!) {
        Connector(id: $connectorId) {
          id
          name
          host
          type
          status
          description
          headers {
            key
            value
          }
        }
      }
    `,
    {
      variables: { connectorId },
    },
  );
};

export const useCreateConnector = () => {
  return useMutation<{ CreateConnector: Connector }, { connector: CreateConnectorInput }>(
    gql`
      mutation CreateConnectorMutation($connector: CreateConnectorInput!) {
        CreateConnector(input: $connector) {
          id
          name
          host
          headers {
            key
            value
          }
          type
          status
          description
        }
      }
    `,
    {
      update(cache, { data }) {
        try {
          const createdConnector = data?.CreateConnector;
          const existingConnectors = cache.readQuery<any>({
            query: GET_ALL_CONNECTORS,
          }).Connectors;

          if (existingConnectors?.edges && createdConnector?.id) {
            cache.writeQuery({
              query: GET_ALL_CONNECTORS,
              data: {
                Connectors: { edges: [...existingConnectors.edges, { node: createdConnector }] },
              },
            });
          }
        } catch (error) {
          console.log('Failed to add the new connector to the existing cache', { error });
        }
      },
    },
  );
};

export const useUpdateConnector = () => {
  return useMutation<Connector, { connector: UpdateConnectorInput }>(
    gql`
      mutation UpdateConnectorMutation($connector: UpdateConnectorInput!) {
        UpdateConnector(input: $connector) {
          id
          name
          host
          headers {
            key
            value
          }
          type
          status
          description
        }
      }
    `,
  );
};
